import { MutableRefObject, useCallback, useEffect, useRef } from "react";
import { BlueprintInfo, BlueprintMinimalInfo } from "./Blueprints";
import { CompaniesApi, CompaniesWithFilter, CompanyInfoApi } from "./Companies";
import { ReportSummaryApi } from "./Reports";
import { WorkflowInfoApi } from "./Workflows";
import { useStorageMap } from "../storage/Storage";
import { ActorInfoApi } from "./Users";

export enum CacheKeys {
  blueprintsInfo = "blueprintsInfo",
  blueprints = "blueprints",
  parentCompanies = "parentCompanies",
  worflowConfigs = "workflowConfigs",
  reports = "reports",
  companies = "companies",
  companiesInfo = "companiesInfo",
  actorInfo = "actorInfo",
}

export type ApplicationCache = {
  blueprintsInfo?: ReturnCache<BlueprintInfo>;
  blueprints?: ReturnCache<BlueprintMinimalInfo>;
  parentCompanies?: ReturnCache<CompaniesWithFilter>[];
  companies?: ReturnCache<CompaniesApi>;
  workflowConfigs?: ReturnCache<WorkflowInfoApi>;
  reports?: ReturnCache<ReportSummaryApi>[];
  companiesInfo?: ReturnCache<CompanyInfoApi[]>;
  actorInfo?: ReturnCache<ActorInfoApi>;
};

export type ReturnCache<T> = {
  type?: string;
  page?: number;
  search?: string;
  items: T[] | T | undefined;
  timestamp: number;
};

export type ApplicationCacheReturnTypes =
  | BlueprintInfo[]
  | BlueprintMinimalInfo[]
  | CompaniesWithFilter
  | WorkflowInfoApi[]
  | CompaniesApi[]
  | ReportSummaryApi
  | CompanyInfoApi[]
  | ActorInfoApi;

export function useCache(): {
  cacheRef: MutableRefObject<ApplicationCache>;
  refreshCache: (
    key: CacheKeys,
    value?: ApplicationCacheReturnTypes,
    page?: number,
    type?: string,
    search?: string
  ) => void;
} {
  const cacheRef = useRef<ApplicationCache>({} as ApplicationCache);
  let counter = useRef<number>(0);
  const { buildCacheInfo, updateCacheRefKey, clearCacheRefKey } =
    useStorageMap();

  const buildApplicationFromCache = useCallback((): ApplicationCache => {
    const cacheInfo = buildCacheInfo();
    return {
      blueprints: cacheInfo.blueprints as ReturnCache<BlueprintMinimalInfo>,
      blueprintsInfo: cacheInfo.blueprintsInfo as ReturnCache<BlueprintInfo>,
      parentCompanies:
        cacheInfo.parentCompanies as ReturnCache<CompaniesWithFilter>[],
      workflowConfigs:
        cacheInfo.workflowConfigs as ReturnCache<WorkflowInfoApi>,
      reports: cacheInfo.reports as ReturnCache<ReportSummaryApi>[],
      companies: cacheInfo.companies as ReturnCache<CompaniesApi>,
      companiesInfo: cacheInfo.companiesInfo as ReturnCache<CompanyInfoApi[]>,
      actorInfo: cacheInfo.actorInfo as ReturnCache<ActorInfoApi>,
    };
  }, [buildCacheInfo]);

  useEffect(() => {
    cacheRef.current = buildApplicationFromCache();
  }, [buildApplicationFromCache]);

  function setCacheWithKeyAndValue(
    updatedCache: ApplicationCache,
    key: CacheKeys
  ) {
    const keysToClearOnRefresh: CacheKeys[] = [];
    if (keysToClearOnRefresh.includes(key)) {
      updateCacheRefKey(key, updatedCache[key], true);
      return;
    }
    updateCacheRefKey(key, updatedCache[key], false);
  }

  function refreshCache(
    key: CacheKeys,
    value?: ApplicationCacheReturnTypes,
    page?: number,
    type?: string,
    search?: string
  ) {
    const updatedCache: ApplicationCache = buildCacheInfo();

    if (!value) {
      clearCacheRefKey(key);
      updatedCache[key] = undefined;
      cacheRef.current = updatedCache;
      return;
    }

    if (key === CacheKeys.blueprints) {
      updatedCache[key] = {
        items: value as BlueprintMinimalInfo[],
        timestamp: Date.now(),
      };
    }

    if (key === CacheKeys.companies) {
      updatedCache[key] = {
        items: value as CompaniesApi[],
        timestamp: Date.now(),
      };
    }

    if (key === CacheKeys.blueprintsInfo) {
      updatedCache[key] = {
        items: value as BlueprintInfo[],
        timestamp: Date.now(),
      };
    }

    if (key === CacheKeys.worflowConfigs) {
      updatedCache[key] = {
        items: value as WorkflowInfoApi[],
        timestamp: Date.now(),
      };
    }

    if (key === CacheKeys.parentCompanies) {
      if (!updatedCache[key]) {
        updatedCache[key] = [];
      }
      updatedCache[key]!.push({
        items: value as CompaniesWithFilter,
        timestamp: Date.now(),
        page: page,
        search: search,
      });
    }

    if (key === CacheKeys.reports) {
      if (!updatedCache[key]) {
        updatedCache[key] = [];
      }
      updatedCache[key]!.push({
        items: value as ReportSummaryApi,
        timestamp: Date.now(),
        page: page,
        type: type,
        search: search,
      });
    }

    if (key === CacheKeys.companiesInfo) {
      updatedCache[key] = {
        items: value as CompanyInfoApi[],
        timestamp: Date.now(),
      };
    }

    if (key === CacheKeys.actorInfo) {
      updatedCache[key] = {
        items: value as ActorInfoApi,
        timestamp: Date.now(),
      };
    }
    setCacheWithKeyAndValue(updatedCache, key);
    cacheRef.current = updatedCache;
  }

  const handleInterval = useCallback(() => {
    counter.current++;
    refreshCache(CacheKeys.reports);
    refreshCache(CacheKeys.actorInfo);
    if (counter.current % 4 === 0) {
      refreshCache(CacheKeys.blueprints);
      refreshCache(CacheKeys.blueprintsInfo);
      refreshCache(CacheKeys.parentCompanies);
      refreshCache(CacheKeys.worflowConfigs);
      refreshCache(CacheKeys.companies);
      refreshCache(CacheKeys.companiesInfo);
      counter.current = 0;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      handleInterval();
    }, 300000);

    return () => clearInterval(interval);
  }, [handleInterval]);

  return { refreshCache, cacheRef };
}
